import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Download, Calendar, BarChart3, TrendingUp, Wrench, Package } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../../components/Card';
import Button from '../../components/Button';
import RowActions from '../../components/RowActions';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import Input from '../../components/Input';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { formatDate } from '../../utils/helpers';
import { downloadSimpleReportPDF } from '../../utils/pdfGeneratorSimple';

const Reports = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [reportType, setReportType] = useState('full');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, report: null });
  const generatorRef = React.useRef(null);

  useEffect(() => {
    loadClients();
    loadReports();
  }, []);

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadReports = async () => {
    try {
      const data = await apiService.getReports();
      setReports(data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedClient) return;

    try {
      setGenerating(true);
      const result = await apiService.generateReport(selectedClient, reportType, startDate || null, endDate || null);
      
      if (result.success || result.data) {
        // Show success message
        toast.success('Rapport généré avec succès ! Téléchargement en cours...');
        
        // Reload reports list
        const updatedReports = await apiService.getReports();
        setReports(updatedReports);
        
        // Find and auto-download the most recent report
        if (updatedReports && updatedReports.length > 0) {
          const latestReport = updatedReports
            .filter(r => r.type === reportType)
            .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))[0];
          
          if (latestReport) {
            // Automatically download it
            handleDownload(latestReport);
          }
        }
        
        // Reset form
        setSelectedClient('');
        setReportType('full');
      } else {
        toast.error('Erreur lors de la génération du rapport');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGenerating(false);
    }
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: `${client.name} - ${client.company}`,
  }));

  const reportTypeOptions = [
    { value: 'full', label: 'Rapport complet' },
    { value: 'equipment', label: 'Liste des équipements' },
    { value: 'maintenance', label: 'Historique des maintenances' },
    { value: 'expiring', label: 'Équipements expirant bientôt' },
  ];

  const getReportTypeLabel = (type) => {
    const option = reportTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  const handlePreview = (report) => {
    navigate('/reports/preview', {
      state: {
        data: JSON.parse(report.fileUrl),
        reportType: report.type,
        clientName: report.clientName,
        clientCompany: report.clientCompany,
        generatedAt: report.generatedAt,
      },
    });
  };

  const handleEditReport = (report) => {
    setSelectedClient(String(report.clientId));
    setReportType(report.type);
    generatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.success('Formulaire pré-rempli — régénérez le rapport si besoin');
  };

  const handleDeleteReport = async () => {
    if (!deleteConfirm.report) return;
    try {
      await apiService.deleteReport(deleteConfirm.report.id);
      setReports((prev) => prev.filter((r) => r.id !== deleteConfirm.report.id));
      toast.success('Rapport supprimé');
      setDeleteConfirm({ open: false, report: null });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownload = (report) => {
    try {
      const data = JSON.parse(report.fileUrl);
      downloadSimpleReportPDF(
        data,
        report.type,
        { name: report.clientName, company: report.clientCompany },
        report.generatedAt
      );
      toast.success('Téléchargement du rapport...');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  // Calculate stats
  const totalReports = reports.length;
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const reportsThisMonth = reports.filter(r => new Date(r.generatedAt) >= firstDayOfMonth).length;
  const fullReports = reports.filter(r => r.type === 'full').length;
  const maintenanceReports = reports.filter(r => r.type === 'maintenance').length;

  // Prepare chart data
  const COLORS = ['#3B82F6', '#10B981', '#A855F7', '#F59E0B', '#EF4444'];

  // Reports by type for pie chart
  const reportsByType = reportTypeOptions.map((type, index) => ({
    name: type.label,
    value: reports.filter(r => r.type === type.value).length,
    color: COLORS[index % COLORS.length]
  })).filter(item => item.value > 0);

  // Reports over last 7 days for line chart
  const getLast7Days = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })
      });
    }
    return days;
  };

  const last7Days = getLast7Days();
  const reportsOverTime = last7Days.map(day => ({
    name: day.label,
    rapports: reports.filter(r => {
      const reportDate = new Date(r.generatedAt).toISOString().split('T')[0];
      return reportDate === day.date;
    }).length
  }));

  // Reports by client for bar chart (top 5)
  const reportsByClient = clients
    .map(client => ({
      name: client.company || client.name,
      rapports: reports.filter(r => r.clientId === client.id).length
    }))
    .filter(item => item.rapports > 0)
    .sort((a, b) => b.rapports - a.rapports)
    .slice(0, 5);

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Rapports</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Générez et téléchargez des rapports</p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-blue-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/30">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Rapports</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalReports}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                Ce mois
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Rapports Récents</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{reportsThisMonth}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-purple-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-purple-500/30">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-purple-600 bg-purple-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalReports > 0 ? Math.round((fullReports / totalReports) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Complets</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{fullReports}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalReports > 0 ? Math.round((maintenanceReports / totalReports) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Maintenances</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{maintenanceReports}</p>
          </div>
        </motion.div>
      </div>

      {/* Charts Section */}
      {reports.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          {/* Reports Over Time - Line Chart */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Évolution des Rapports</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">7 derniers jours</p>
              </div>
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={reportsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#9CA3AF"
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="rapports" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Reports by Type - Pie Chart */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Types de Rapports</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Distribution par type</p>
              </div>
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={reportsByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {reportsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {reportsByType.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-xs sm:text-sm text-gray-700">{entry.name}</span>
                    <span className="text-xs sm:text-sm font-semibold text-gray-900">({entry.value})</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Reports by Client - Bar Chart */}
          {reportsByClient.length > 0 && (
            <Card className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top 5 Clients</h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">Clients avec le plus de rapports</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportsByClient}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    stroke="#9CA3AF"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#9CA3AF"
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="rapports" 
                    fill="#10B981"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* Generate Report */}
      <div ref={generatorRef}>
      <Card>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Générer un rapport</h2>
        <div className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <Select
              label="Client"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              options={clientOptions}
              placeholder="Sélectionner un client"
            />
            <Select
              label="Type de rapport"
              name="reportType"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              options={reportTypeOptions}
              placeholder="Sélectionner le type de rapport"
            />
          </div>
          
          {/* Date Range Filter */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Période du rapport (optionnel)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <Input
                label="Date de début"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                helpText="Laisser vide pour tout inclure"
              />
              <Input
                label="Date de fin"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                helpText="Laisser vide pour tout inclure"
              />
            </div>
            {(startDate || endDate) && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>
                    {startDate && endDate
                      ? `Rapport du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`
                      : startDate
                      ? `Rapport à partir du ${new Date(startDate).toLocaleDateString('fr-FR')}`
                      : `Rapport jusqu'au ${new Date(endDate).toLocaleDateString('fr-FR')}`}
                  </span>
                </p>
              </div>
            )}
          </div>

          <Button
            variant="primary"
            icon={FileText}
            onClick={handleGenerate}
            loading={generating}
            disabled={!selectedClient || generating}
            className="w-full sm:w-auto"
          >
            Générer le rapport
          </Button>
        </div>
      </Card>
      </div>

      {/* Recent Reports */}
      <Card>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Rapports récents</h2>
        <div className="space-y-3">
          {reports.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm sm:text-base text-gray-500">Aucun rapport généré</p>
            </div>
          ) : (
            reports.map((report, index) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                  <div className="p-2 sm:p-3 bg-primary-50 rounded-lg flex-shrink-0">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{getReportTypeLabel(report.type)}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      Client: {report.clientName}
                      {report.clientCompany && ` - ${report.clientCompany}`}
                    </p>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{formatDate(new Date(report.generatedAt), 'dd/MM/yyyy HH:mm')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  <RowActions
                    onView={() => handlePreview(report)}
                    onEdit={() => handleEditReport(report)}
                    onDelete={() => setDeleteConfirm({ open: true, report })}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Download}
                    onClick={() => handleDownload(report)}
                  >
                    PDF
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </Card>
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, report: null })}
        onConfirm={handleDeleteReport}
        title="Supprimer le rapport"
        message={`Supprimer le rapport "${getReportTypeLabel(deleteConfirm.report?.type)}" de ${deleteConfirm.report?.clientName} ?`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  );
};

export default Reports;

